import { useState } from 'react';
import type { WeeklyPlanDay } from '../../../services/scheduleService';

// Compact day picker (Mon–Sun since JIM-1030-08) used by the Schedule tab's reschedule action and
// by the Maps tab's pin popover. Both surfaces share the same picker so the
// "schedule for another day" UX stays in one place (F6 reuses the existing
// reschedule UI rather than duplicating it).
export function DayPicker({
  weekDays,
  currentDate,
  onSelect,
  onClose,
  allowEveryWeek = false,
}: {
  weekDays: WeeklyPlanDay[];
  currentDate?: string;
  onSelect: (date: string, everyWeek?: boolean) => void;
  onClose: () => void;
  /** Show the "Every week" choice (Jim 2026-08-21: one-week move vs
   * changing the site's standing day). */
  allowEveryWeek?: boolean;
}) {
  const [everyWeek, setEveryWeek] = useState(false);
  return (
    <div className="inline-flex flex-col gap-1 bg-slate-50 dark:bg-slate-700/50 rounded-lg p-1.5">
      <div className="flex items-center gap-1 flex-wrap">
        {weekDays.map((d) => {
          const isCurrentDay = d.date === currentDate;
          return (
            <button
              key={d.date}
              onClick={() => {
                if (!isCurrentDay) onSelect(d.date, everyWeek);
              }}
              disabled={isCurrentDay}
              className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${
                isCurrentDay
                  ? 'bg-slate-300 dark:bg-slate-600 text-slate-500 dark:text-slate-400 cursor-not-allowed'
                  : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:text-primary-700 dark:hover:text-primary-300 shadow-sm'
              }`}
            >
              {d.day_of_week.slice(0, 3)}
            </button>
          );
        })}
        <button
          onClick={onClose}
          className="px-2 py-1.5 text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
        >
          ×
        </button>
      </div>
      {allowEveryWeek && (
        <label className="flex items-center gap-1.5 px-1 text-[11px] text-slate-500 dark:text-slate-400 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={everyWeek}
            onChange={(e) => setEveryWeek(e.target.checked)}
            className="w-3 h-3 rounded border-slate-300 dark:border-slate-600 text-primary-600 focus:ring-primary-500"
          />
          Every week from now on
        </label>
      )}
    </div>
  );
}
